/**
 * Deposit Modal Manager
 * 
 * Handles opening, closing, and state management for the wallet deposit modal.
 * Follows Single Responsibility Principle - only manages modal UI state.
 * 
 * @package betbeast
 */

import { DepositExitIntentModal } from './deposit-exit-intent-modal.js';
import { getCasinoRedirectUrl } from '../account-redirect-utils.js';

export class DepositModal {
    constructor(modalId = 'wallet-deposit-modal', exitIntentModal = null) {
        this.modal = document.getElementById(modalId);
        this.modalClass = 'wallet-deposit-modal';
        this.bodyClass = 'wallet-deposit-modal-open';
        this.isOpen = false;
        this.hasDeposited = false;
        this.depositCompleted = false;
        this.exitIntentModal = exitIntentModal || new DepositExitIntentModal();
        
        if (!this.modal) {
            console.warn(`Deposit modal with ID "${modalId}" not found`);
            return;
        }
        
        this.init();
        this.initExitIntentListeners();
    }
    
    /**
     * Initialize modal event listeners
     */
    init() {
        // Close button handlers (X buttons only — overlay click and Escape do not close)
        const closeButtons = this.modal.querySelectorAll('[data-wallet-deposit-modal-close]');
        closeButtons.forEach(button => {
            button.addEventListener('click', () => this.handleCloseAttempt());
        });

        // Mobile bonus toggle
        const mobileToggle = this.modal.querySelector('[data-mobile-bonus-toggle]');
        if (mobileToggle) {
            mobileToggle.addEventListener('click', () => this.toggleMobileBonusView());
        }

        // Listen for deposit_complete postMessage from the payment result iframe
        window.addEventListener('message', (event) => {
            const data = event.data;
            if (!data) return;
            if (data.type === 'deposit_complete') {
                // A failed result must NOT suppress exit intent — only pending/success mark the
                // deposit as done. On failure we leave the flags false so handleCloseAttempt()
                // still shows the exit-intent modal.
                if ((data.status || '').toLowerCase() !== 'error') {
                    this.depositCompleted = true;
                    this.hasDeposited = true;
                }
            }
            if (data.type === 'deposit_reopen') {
                // User clicked "Try Again" on the result page — dispatch event for PaymentHandler
                // to handle (it cannot be called directly here as modal doesn't hold a reference)
                this.modal.dispatchEvent(new CustomEvent('deposit_request_back_to_amount', { bubbles: true }));
                if (!this.isOpen) this.open();
            }
        });

        // Listen for inline result completion (direct-success, no iframe postMessage)
        this.modal.addEventListener('deposit_inline_complete', (event) => {
            this.depositCompleted = true;
            this.hasDeposited = true;
        });
    }
    
    /**
     * Initialize exit intent modal listeners
     */
    initExitIntentListeners() {
        // Listen for return to deposit event
        document.addEventListener('returnToDeposit', () => {
            // Return to the amount form so a failed/stale payment iframe can't reappear
            // (breaks the exit-intent ⇄ failed-iframe loop).
            this.modal.dispatchEvent(new CustomEvent('deposit_request_back_to_amount', { bubbles: true }));
            this.modal.classList.remove('is-hidden');
        });
        
        // Listen for exit intent dismissed event
        document.addEventListener('exitIntentDismissed', () => {
            this.forceClose();
        });
    }
    
    /**
     * Handle close attempt - show exit intent if user hasn't deposited
     */
    handleCloseAttempt() {
        if (this.depositCompleted) {
            // Deposit finished – skip exit intent and redirect to casino
            this.forceClose();
            window.location.href = getCasinoRedirectUrl();
            return;
        }
        if (!this.hasDeposited && this.exitIntentModal) {
            // Always hide deposit modal and show exit intent modal when no deposit completed
            this.modal.classList.add('is-hidden');
            this.exitIntentModal.open();
        } else {
            // User has deposited or already seen exit intent, close normally
            this.forceClose();
        }
    }
    
    /**
     * Open the modal
     */
    open() {
        if (!this.modal) return;
        
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add(this.bodyClass);
        this.isOpen = true;
        
        // Reset deposit state when opening. depositCompleted must reset too, otherwise a
        // completed flag from a previous attempt makes every reopened close redirect to casino.
        this.hasDeposited = false;
        this.depositCompleted = false;
        if (this.exitIntentModal) {
            this.exitIntentModal.reset();
        }
        
        // Focus management for accessibility
        const firstFocusable = this.modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }
    
    /**
     * Close the modal (with exit intent check)
     */
    close() {
        this.handleCloseAttempt();
    }
    
    /**
     * Force close the modal without exit intent
     */
    forceClose() {
        if (!this.modal) return;
        
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove(this.bodyClass);
        this.isOpen = false;
    }
    
    /**
     * Mark deposit as completed (prevents exit intent from showing)
     */
    markDepositCompleted() {
        this.hasDeposited = true;
    }
    
    /**
     * Toggle mobile bonus view (show bonuses, hide form or vice versa)
     */
    toggleMobileBonusView() {
        if (!this.modal) return;
        
        this.modal.classList.toggle('wallet-deposit-mobile-bonus-expanded');
    }
    
    /**
     * Check if modal is currently open
     */
    isModalOpen() {
        return this.isOpen;
    }
}
